import { createClient } from '@supabase/supabase-js';
import { config } from './config';
import { chunk } from './utils';
import { readPasswordLink } from './auth-links';
import { supabaseFetch } from './supabase-transport';
import { managedRecordingKey } from '../../../../backend-cloud/shared/recording-links';
import { APP_API_ORIGIN, appRecordingKey } from './app-recordings';

export const databaseFetch = typeof window !== 'undefined' && config.supabaseUrl && !config.isDemo
  ? supabaseFetch(config.supabaseUrl, window.location.origin)
  : fetch;

export const passwordLinkContext = readPasswordLink(
  typeof window === 'undefined' ? { search: '', hash: '' } : window.location,
);

export const supabase = createClient(
  config.supabaseUrl || 'https://placeholder.supabase.co',
  config.supabaseAnonKey || 'placeholder-anon-key',
  {
    global: { fetch: databaseFetch },
    auth: {
      autoRefreshToken: !config.isDemo,
      persistSession: !config.isDemo,
      detectSessionInUrl: !config.isDemo,
      // Existing email templates (confirm signup, recovery) deliver tokens in the
      // URL hash, so stay on the implicit flow.
      flowType: 'implicit',
    },
  },
);

export class DbError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

interface PgResult<T> {
  data: T | null;
  error: { message: string; code?: string } | null;
}

export function unwrap<T>(result: PgResult<T>): T {
  if (result.error) throw new DbError(result.error.message, result.error.code);
  return result.data as T;
}

/**
 * PostgREST caps a response at 1000 rows by default. Anything that must see
 * every row (exports, bulk deletes) pages through with this.
 */
export async function fetchAllRows<T>(
  page: (from: number, to: number) => PromiseLike<PgResult<unknown>>,
  { pageSize = 1000, max = 200_000 }: { pageSize?: number; max?: number } = {},
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; from < max; from += pageSize) {
    const batch = (unwrap(await page(from, from + pageSize - 1)) as T[] | null) ?? [];
    rows.push(...batch);
    if (batch.length < pageSize) break;
  }
  return rows;
}

async function readFunctionError(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.clone().json();
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    } catch {
      /* not JSON */
    }
  }
  return (error as Error)?.message || 'Request failed';
}

/** Call a Supabase Edge Function and surface its own error message. */
export async function invokeFunction<T = unknown>(name: string, body: Record<string, unknown>): Promise<T> {
  if (config.recordingsApi && ['fcm-reviver','delete-r2-files'].includes(name)) {
    const {data}=await supabase.auth.getSession();
    if(!data.session)throw new Error('Your session expired. Please sign in again.');
    const response=await fetch(`${config.recordingsApi}/${name}`,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${data.session.access_token}`},body:JSON.stringify(body)});
    const result=await response.json() as {error?:string};
    if(!response.ok)throw new Error(result.error||'Request failed');
    return result as T;
  }
  if (config.isSandbox) {
    const missing: Record<string, string> = {
      'fcm-reviver': 'Android push sync is not connected to this test backend.',
      'send-reset-password': 'Password-reset email delivery is not connected to this test backend.',
    };
    if (missing[name]) throw new Error(missing[name]);
  }
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw new Error(await readFunctionError(error));
  return data as T;
}

/** Call one of this app's own /api routes (Cloudflare Pages Functions). */
export async function apiPost<T = unknown>(path: string, body: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session expired. Please sign in again.');

  let res: Response;
  try {
    res = await fetch(`${config.apiBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Could not reach the server. Check your connection and try again.');
  }

  let payload: Record<string, unknown> = {};
  try {
    payload = await res.json();
  } catch {
    /* empty or non-JSON body */
  }
  if (!res.ok || payload.ok === false) {
    const message = (payload.error as string) || (payload.message as string);
    throw new Error(
      message ||
        (res.status === 404
          ? 'This feature is not available on this deployment yet.'
          : `Request failed (${res.status})`),
    );
  }
  return payload as T;
}

const R2_HOST = 'r2.callsyncapp.com';

/** Remove R2 files. Strict mode requires every file to be confirmed before DB cleanup. */
export async function deleteRecordingFiles(
  urls: (string | null | undefined)[],
  options: { strict?: boolean } = {},
): Promise<void> {
  const sandboxKeys: string[] = [];
  const appKeys: string[] = [];
  const keys = urls
    .map((url) => {
      try {
        if (!url) return null;
        const parsed = new URL(url);
        const appKey = appRecordingKey(url);
        if (appKey) {
          appKeys.push(appKey);
          return null;
        }
        if (
          config.isSandbox &&
          parsed.origin === new URL(config.supabaseUrl).origin &&
          parsed.pathname.startsWith('/storage/v1/object/public/sandbox-recordings/')
        ) {
          sandboxKeys.push(
            decodeURIComponent(parsed.pathname.slice('/storage/v1/object/public/sandbox-recordings/'.length)),
          );
          return null;
        }
        const managedKey = managedRecordingKey(url);
        if (managedKey) return managedKey;
        if (config.recordingsApi && parsed.origin === new URL(config.recordingsApi).origin && parsed.pathname === '/v1/recordings/file')
          return parsed.searchParams.get('key');
        if (parsed.hostname !== R2_HOST) return null;
        return decodeURIComponent(parsed.pathname.replace(/^\/+/, '')) || null;
      } catch {
        return null;
      }
    })
    .filter((k): k is string => Boolean(k));

  if (options.strict && keys.length + sandboxKeys.length + appKeys.length !== urls.length)
    throw new Error(
      'Some recording files could not be located. They have not been removed from your call history.',
    );

  for (const batch of chunk([...new Set(appKeys)], 200)) {
    try {
      const {data} = await supabase.auth.getSession();
      if (!data.session) throw new Error('Sign in to delete these recordings.');
      const response = await fetch(`${APP_API_ORIGIN}/v1/recordings/delete`, {
        method: 'POST', headers: {'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}`},
        body: JSON.stringify({keys: batch}),
      });
      const result = await response.json() as {results?: {key: string; success: boolean}[]; error?: string};
      if (!response.ok || batch.some(key => !result.results?.some(item => item.key === key && item.success)))
        throw new Error(result.error || 'Some files could not be deleted. Retry the remaining recordings.');
    } catch (error) {
      if (options.strict) throw error;
      console.warn('[recordings] App API delete failed for a batch', error);
    }
  }

  for (const batch of chunk([...new Set(sandboxKeys)], 200)) {
    const { error } = await supabase.storage.from('sandbox-recordings').remove(batch);
    if (error && options.strict) throw error;
    if (error) console.warn('[recordings] Test storage delete failed', error);
  }

  for (const batch of chunk([...new Set(keys)], 200)) {
    try {
      const result = await invokeFunction<{ results?: { key: string; success: boolean }[] }>(
        'delete-r2-files',
        { keys: batch },
      );
      if (
        options.strict &&
        batch.some((key) => !result?.results?.some((item) => item.key === key && item.success === true))
      ) {
        throw new Error('Some files could not be deleted. Retry the remaining recordings.');
      }
    } catch (err) {
      if (options.strict) throw err;
      console.warn('[recordings] R2 delete failed for a batch', err);
    }
  }
}
